import { Percent, Scale, ShoppingCart } from "lucide-react";
import {
  GuideStepStrip,
  PublicFooter,
  PublicHeader,
  PublicSignupCTA,
} from "../components/PublicPageChrome";

const YIELD_STEPS = [
  { label: "What you buy", icon: ShoppingCart },
  { label: "What's actually usable", icon: Percent },
  { label: "Your real cost", icon: Scale },
];

export default function YieldGuidePage() {
  return (
    <div className="min-h-screen bg-cream text-ink font-body">
      <PublicHeader maxWidthClass="max-w-3xl" />

      <main className="max-w-3xl mx-auto px-5 pb-24 space-y-10 animate-rise">
        <div>
          <a href="/what-should-i-charge" className="text-sm font-medium text-tea-text hover:underline">
            ← Back to the price-floor calculator
          </a>
          <div className="mt-6 max-w-2xl">
            <p className="text-xs uppercase tracking-[0.2em] font-medium text-tea-text">Costing a quote · Yield</p>
            <h1 className="font-heading font-bold text-4xl sm:text-5xl tracking-tight mt-2 [text-wrap:balance]">
              The price on the receipt isn't what you're actually paying per kg.
            </h1>
            <p className="mt-4 text-ink/60 text-base sm:text-lg [text-wrap:pretty]">
              Shells, stones, cores, peel, and trim can leave you with less usable food than you paid for. That makes
              every usable kilogram more expensive than the number printed on the receipt.
            </p>
          </div>
        </div>

        <section aria-labelledby="yield-chain-heading" className="space-y-4">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] font-medium text-ink/50">The real-cost chain</p>
            <h2 id="yield-chain-heading" className="font-heading font-bold text-2xl sm:text-3xl tracking-tight mt-1 [text-wrap:balance]">
              Start with what you bought. Cost what you can use.
            </h2>
          </div>
          <GuideStepStrip label="The three steps of yield costing" steps={YIELD_STEPS} />
        </section>

        <section aria-labelledby="pineapple-heading" className="space-y-5">
          <div className="max-w-2xl">
            <p className="text-xs uppercase tracking-[0.2em] font-medium text-ink/50">Worked example · one pineapple, two dishes</p>
            <h2 id="pineapple-heading" className="font-heading font-bold text-2xl sm:text-3xl tracking-tight mt-1 [text-wrap:balance]">
              The same £3 pineapple has two different real costs.
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-ink/60">
              Say a 1.5 kg pineapple costs £3.00. What counts as usable changes with what you are making.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <article className="bg-white border border-sand rounded-lg p-5 sm:p-6">
              <p className="text-xs uppercase tracking-[0.2em] font-medium text-tea-text">Fruit platter · 90% usable</p>
              <h3 className="font-heading font-semibold text-xl mt-2">Most of it earns its place.</h3>
              <p className="mt-3 text-sm leading-relaxed text-ink/60">
                The rind stays on the slices, the crown becomes part of the presentation, and only the woody core is lost.
              </p>
              <div className="mt-5 border-t border-sand pt-5 space-y-2 font-mono-num text-sm text-ink/60">
                <p>1.5 kg × 90% = 1.35 kg usable</p>
                <p>£3.00 ÷ 1.35 kg</p>
                <p className="pt-1 text-2xl font-semibold text-ink">£2.22/kg</p>
              </div>
            </article>

            <article className="bg-white border border-sand rounded-lg p-5 sm:p-6">
              <p className="text-xs uppercase tracking-[0.2em] font-medium text-almond">Juice · 55% usable</p>
              <h3 className="font-heading font-semibold text-xl mt-2">Much more is removed.</h3>
              <p className="mt-3 text-sm leading-relaxed text-ink/60">
                The rind, crown, eyes, and core are discarded. Only the pulp going into the juice counts as usable.
              </p>
              <div className="mt-5 border-t border-sand pt-5 space-y-2 font-mono-num text-sm text-ink/60">
                <p>1.5 kg × 55% = 0.825 kg usable</p>
                <p>£3.00 ÷ 0.825 kg</p>
                <p className="pt-1 text-2xl font-semibold text-ink">£3.64/kg</p>
              </div>
            </article>
          </div>
        </section>

        <aside className="rounded-lg bg-ink p-6 sm:p-8 text-white">
          <p className="text-xs uppercase tracking-[0.2em] font-medium text-white/60">The one-line test</p>
          <p className="mt-3 text-base leading-relaxed text-white/80 [text-wrap:pretty]">
            If no dish could ever use a part of it, such as a stone, shell, or core, it is genuinely lost every time.
            If some dishes use it and others do not, such as peel kept on a platter but discarded for juice, the
            usable fraction depends on what you are making. Do not apply one fixed number to every recipe.
          </p>
        </aside>

        <aside className="rounded-lg border border-sand bg-tea/10 p-5 sm:p-6">
          <p className="text-sm leading-relaxed text-ink/70 [text-wrap:pretty]">
            <span className="font-semibold text-ink">Mnandi stores one usable-percentage figure per ingredient and applies it automatically to every recipe cost.</span>{" "}
            Set it once when you add an ingredient, not per recipe.
          </p>
        </aside>

        <div>
          <a href="/learn/labour-costing" className="text-sm font-medium text-tea-text hover:underline">
            Also: count all of your labour →
          </a>
        </div>

        <PublicSignupCTA
          eyebrow="Cost the food you can actually use"
          heading="Let the real ingredient cost follow every recipe."
          body="Save each ingredient's price and usable percentage once. Mnandi will carry the true unit cost into your recipes and quotes automatically."
          testId="yield-guide-signup-cta"
        />
      </main>

      <PublicFooter maxWidthClass="max-w-3xl" />
    </div>
  );
}
